import { RouteProp } from '@react-navigation/native';
import React, { useMemo } from 'react';
import { ProfilesTabParamList, useGoBack, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import { useCollaborators } from '../../../../../hooks/api/useCollaborators';
import getName from '../../../../../utils/getName';
import getDateString from '../../../../../utils/getDateString';
import { getIntl } from '../../../../../utils/intl';
import { Field } from 'formik';
import { ABAC } from '../../../../../primary-record-types';
import { useHasPermission } from '../../../../v2BaseComponents/Permissions/hooks';
import useMedicalProfile from '../../../../../hooks/api/useMedicalProfile';
import ConsentField from './ConsentField';
import { handleFormikSubmit } from '../../../../../utils/handleFormikSubmit';
import { useConfirmBackNavigationOnDirtyForm } from '../../../../shared/FormExitValidation';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { CenteredFormRow, Form } from '../../../../v2BaseComponents/Form';
import Stack, { FormStack } from '../../../../v2BaseComponents/Stack';
import { IconAvatar } from '../../../../v2BaseComponents/Avatar';
import Text from '../../../../v2BaseComponents/Text';
import { getSpacing } from '../../../../../utils/spacing';
import { FormikInputs } from '../../../../v2BaseComponents/Inputs';
import useEvaluatedPermissions from '../../../../../hooks/api/useEvaluatedPermissions';

const intl = getIntl('profile-invitation-details-form', {
    invitationSent: 'Invitation Sent {dateTime}',
    consent:
        'As a Profile Organizer or Collaborator, I, {adminName}, confirm I have the permission and right to share access to {profileName}’s Medical Profile to {inviteeName}.',
    update: 'Update Invitation',
    delete: 'Revoke Invitation',
    resend: 'Resend',
    thisPerson: 'this person',
});

export type InvitationDetailsScreenOwnProps = {};

export type InvitationDetailsScreenProps = InvitationDetailsScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'invite-details'>;
};

export type FormValue = {
    appPermissions: ABAC.AppPermissions;
    acceptConsent: boolean;
};

const InvitationDetailsScreen: React.FC<InvitationDetailsScreenProps> = ({
    route: {
        params: { profileId, invitationId },
    },
}) => {
    const { permissions } = useEvaluatedPermissions({ medicalProfileId: profileId });
    const goBack = useGoBack();
    const navigation = useProfilesTabNavigation();
    const hasCollaboratorEdit = useHasPermission(profileId, 'collaborators', 'edit');
    useConfirmBackNavigationOnDirtyForm(navigation);
    const {
        collaboratorInvites,
        isLoadingCollaboratorInvites,
        isUpdatingCollaboratorInvite,
        updateCollaboratorInvite,
        deleteCollaboratorInvite,
        isDeletingCollaboratorInvite,
        resendInvite,
    } = useCollaborators({
        medicalProfileId: profileId,
        onUpdateInviteSuccess: () => goBack(),
        onDeleteInviteSuccess: () => goBack(),
    });
    const invite = (collaboratorInvites || []).find(
        (invite) => invite.medicalProfileCollaboratorInviteId === invitationId,
    );
    const initialValues = useMemo<FormValue>(
        () => ({
            appPermissions: invite?.appPermissions || ({} as any),
            acceptConsent: true,
        }),
        [invite],
    );
    const { profile } = useMedicalProfile({ medicalProfileId: profileId });
    const profileName = getName({
        firstName: profile?.contactDetails?.firstName,
        lastName: profile?.contactDetails?.lastName,
    });
    const adminName = getName({
        firstName: invite?.invitedByFirstName,
        lastName: invite?.invitedByLastName,
    });
    const inviteeName = getName({
        firstName: invite?.inviteeFirstName,
        lastName: invite?.inviteeLastName,
    });

    const isLoading = isLoadingCollaboratorInvites || isUpdatingCollaboratorInvite || isDeletingCollaboratorInvite;

    const isDisabled = !hasCollaboratorEdit;

    return (
        <AnimateLoadedContent isLoading={isLoading || !invite}>
            {() => (
                <Form
                    actionContainerSubmit
                    saveText={intl('update')}
                    initialValues={initialValues}
                    onCancel={goBack}
                    cancelButtonVariant="secondary"
                    onSubmit={(values, helpers) => {
                        updateCollaboratorInvite({
                            inviteId: invitationId,
                            update: { appPermissions: values.appPermissions },
                        });
                        handleFormikSubmit(values, helpers);
                    }}
                    isDisabled={!hasCollaboratorEdit}
                    nonDirtyActions={[
                        {
                            title: intl('resend'),
                            icon: 'Send',
                            testID: 'resend-invitation-button',
                            onPress: () => {
                                resendInvite(invitationId);
                            },
                            show: hasCollaboratorEdit,
                        },
                        {
                            title: intl('delete'),
                            icon: 'Trash',
                            testID: 'delete-invitation-button',
                            onPress: () => {
                                deleteCollaboratorInvite(invitationId);
                            },
                            show: hasCollaboratorEdit,
                        },
                    ]}
                >
                    {({ values, setFieldValue }) => (
                        <FormStack>
                            <Stack
                                horizontal
                                align="center"
                                space={getSpacing(2.5)}
                                style={{
                                    marginBottom: getSpacing(4),
                                }}
                            >
                                <IconAvatar icon="Send" />
                                <Stack vertical justify="center">
                                    <Text variant="h6">
                                        {getName({
                                            firstName: invite!.invitedByFirstName,
                                            lastName: invite!.invitedByLastName,
                                        })}
                                    </Text>
                                    <Text variant="caption-1">{invite!.email}</Text>
                                    <Text variant="caption-2">
                                        {intl('invitationSent', {
                                            dateTime: getDateString(invite!.createDate, 'date-time'),
                                        })}
                                    </Text>
                                </Stack>
                            </Stack>
                            <CenteredFormRow>
                                <Field
                                    component={FormikInputs.PermissionsInput}
                                    name="appPermissions"
                                    isDisabled={isDisabled}
                                    showEditCollaboratorWarning
                                    mostPermissiveAppPermissions={permissions.permissions}
                                    initialAppPermissions={invite!.appPermissions}
                                />
                            </CenteredFormRow>
                            <CenteredFormRow>
                                <ConsentField
                                    title={intl('consent', {
                                        adminName,
                                        profileName,
                                        inviteeName: inviteeName || intl('thisPerson'),
                                    })}
                                    onChange={(value) => setFieldValue('acceptConsent', value)}
                                    formValues={values}
                                    isDisabled={isDisabled}
                                />
                            </CenteredFormRow>
                        </FormStack>
                    )}
                </Form>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(InvitationDetailsScreen);
